import ApartmentIcon from '@mui/icons-material/Apartment'
import ArticleIcon from '@mui/icons-material/Article'
import BusinessIcon from '@mui/icons-material/Business'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import FactoryIcon from '@mui/icons-material/Factory'
import HandshakeIcon from '@mui/icons-material/Handshake'
import HelpCenterIcon from '@mui/icons-material/HelpCenter'
import InfoIcon from '@mui/icons-material/Info'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import NewspaperIcon from '@mui/icons-material/Newspaper'
import QuizIcon from '@mui/icons-material/Quiz'
import SellIcon from '@mui/icons-material/Sell'
import StoreIcon from '@mui/icons-material/Store'
import StraightenIcon from '@mui/icons-material/Straighten'
import UploadIcon from '@mui/icons-material/Upload'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import { PillarPage, SearchPageCity } from 'src/constants/searchPages'
import type { INotificationCounts } from 'src/hooks/useNotificationCounts'
import { RedirectUrls } from 'src/types/types'
import { HygraphTopics } from 'src/utils/hygraph'

/**
 * Path to the Almacenamiento a Domicilio (on-demand) pillar landing.
 *
 * Living under the SelfStorage pillar means navigating to this URL highlights
 * the same Servicios menu as `/renta-mini-bodegas/*` — no extra active-state
 * wiring required.
 */
export const ON_DEMAND_PATH = `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio`

/**
 * Path to the Flotillas (B2B fleet parking) landing.
 *
 * Lives under the Parking pillar so navigating here highlights the same
 * Servicios menu as `/pension-estacionamiento/*`.
 */
export const FLEET_PATH = `/${PillarPage.Parking}/flotillas`

/** Marker used by mega-menu / drawer renderers to gate visibility on
 *  `hasAnyOnDemandCoverage()`. */
export type MenuGate = 'on-demand-coverage'

export const MENU_ITEMS = [
  {
    id: 'storage',
    translationKey: 'MENU.STORAGE',
    image: {
      src: '/images/flotillas.jpg',
      labelKey: 'MENU.STORAGE.FEATURED',
      descriptionKey: 'MENU.STORAGE.FEATURED_DESCRIPTION',
      href: FLEET_PATH,
      tone: 'primary' as const
    },
    columns: [
      {
        translationKey: 'SECTIONS.STORAGE_TYPES',
        items: [
          {
            translationKey: 'ITEMS.MINI_STORAGE',
            href: `/${PillarPage.SelfStorage}`,
            icon: WarehouseIcon
          },
          {
            translationKey: 'ITEMS.ON_DEMAND',
            href: ON_DEMAND_PATH,
            icon: LocalShippingIcon,
            gateBy: 'on-demand-coverage' as MenuGate
          },
          {
            translationKey: 'ITEMS.PARKING',
            href: `/${PillarPage.Parking}`,
            icon: LocalParkingIcon
          },
          {
            translationKey: 'ITEMS.COMMERCIAL_WAREHOUSE',
            href: `/${PillarPage.Warehouse}`,
            icon: StoreIcon
          },
          {
            translationKey: 'ITEMS.INDUSTRIAL',
            href: `/${PillarPage.Industrial}`,
            icon: FactoryIcon
          },
          {
            translationKey: 'ITEMS.LOGISTICS',
            href: RedirectUrls.SolucionesLogisticas,
            icon: Inventory2Icon
          }
        ]
      },
      {
        translationKey: 'SECTIONS.LOCATIONS',
        items: [
          {
            translationKey: 'ITEMS.MONTERREY',
            href: `/${PillarPage.SelfStorage}/${SearchPageCity.Monterrey}`,
            icon: LocationOnIcon,
            /** A city name needs no explainer line. */
            noDescription: true
          },
          {
            translationKey: 'ITEMS.GUADALAJARA',
            href: `/${PillarPage.SelfStorage}/${SearchPageCity.Guadalajara}`,
            icon: LocationOnIcon,
            /** A city name needs no explainer line. */
            noDescription: true
          },
          {
            translationKey: 'ITEMS.CDMX',
            href: `/${PillarPage.SelfStorage}/${SearchPageCity.CiudadDeMexico}`,
            icon: LocationOnIcon,
            /** A city name needs no explainer line. */
            noDescription: true
          },
          {
            translationKey: 'ITEMS.QUERETARO',
            href: `/${PillarPage.SelfStorage}/${SearchPageCity.Queretaro}`,
            icon: LocationOnIcon,
            /** A city name needs no explainer line. */
            noDescription: true
          },
          {
            translationKey: 'ITEMS.MORE_CITIES',
            href: `/${PillarPage.SelfStorage}`,
            icon: LocationOnIcon
          }
        ]
      }
    ]
  },
  {
    id: 'offer',
    translationKey: 'MENU.OFFER',
    image: {
      src: '/images/Bodega.webp',
      labelKey: 'MENU.OFFER.FEATURED',
      descriptionKey: 'MENU.OFFER.FEATURED_DESCRIPTION',
      href: RedirectUrls.Anfitrion,
      tone: 'primary' as const
    },
    columns: [
      {
        translationKey: 'SECTIONS.MONETIZE',
        items: [
          {
            translationKey: 'ITEMS.UPLOAD',
            href: RedirectUrls.Anfitrion,
            icon: UploadIcon
          },
          {
            translationKey: 'ITEMS.REFER',
            href: RedirectUrls.Refer,
            icon: ContactMailIcon
          },
          {
            translationKey: 'ITEMS.VALUE',
            href: RedirectUrls.Calculate,
            icon: SellIcon
          },
          {
            translationKey: 'ITEMS.BROKER',
            href: RedirectUrls.Broker,
            icon: HandshakeIcon
          }
        ]
      },
      {
        translationKey: 'SECTIONS.BUSINESS_SOLUTIONS',
        items: [
          {
            translationKey: 'ITEMS.BUSINESS',
            href: RedirectUrls.Negocio,
            icon: BusinessIcon
          },
          {
            translationKey: 'ITEMS.TRADITIONAL',
            href: RedirectUrls.Tradicional,
            icon: StoreIcon
          },
          {
            translationKey: 'ITEMS.PARKING_HOST',
            href: RedirectUrls.Estacionamiento,
            icon: LocalParkingIcon
          },
          {
            translationKey: 'ITEMS.BODEGAS_HOST',
            href: RedirectUrls.Bodegas,
            icon: WarehouseIcon
          },
          {
            translationKey: 'ITEMS.LOGISTICS_HOST',
            href: RedirectUrls.AnfitrionLogistica,
            icon: Inventory2Icon
          },
          {
            translationKey: 'ITEMS.DEVELOPERS',
            href: RedirectUrls.Desarrolladores,
            icon: ApartmentIcon
          }
        ]
      }
    ]
  },
  {
    id: 'resources',
    translationKey: 'MENU.RESOURCES',
    image: {
      src: '',
      labelKey: 'MENU.RESOURCES.FEATURED',
      descriptionKey: 'MENU.RESOURCES.FEATURED_DESCRIPTION',
      href: RedirectUrls.Help,
      tone: 'secondary' as const
    },
    columns: [
      {
        translationKey: 'SECTIONS.HELP',
        items: [
          {
            translationKey: 'ITEMS.HELP_CENTER',
            href: RedirectUrls.Help,
            icon: HelpCenterIcon
          },
          {
            translationKey: 'ITEMS.FAQ',
            href: `${RedirectUrls.Help}/${HygraphTopics.HelpFaq}/preguntas-frecuentes`,
            icon: QuizIcon
          },
          {
            translationKey: 'ITEMS.CONTACT',
            href: RedirectUrls.Contact,
            icon: ContactSupportIcon
          },
          {
            translationKey: 'ITEMS.SIZE_GUIDE',
            href: RedirectUrls.SizeGuide,
            icon: StraightenIcon
          },
          {
            translationKey: 'ITEMS.SEGURO',
            href: RedirectUrls.Seguro,
            icon: VerifiedUserIcon
          }
        ]
      },
      {
        translationKey: 'SECTIONS.BLOG',
        items: [
          {
            translationKey: 'ITEMS.BLOG',
            href: RedirectUrls.Blog,
            icon: ArticleIcon
          },
          {
            translationKey: 'ITEMS.ABOUT',
            href: RedirectUrls.Nosotros,
            icon: InfoIcon
          },
          {
            translationKey: 'ITEMS.MEDIA',
            href: RedirectUrls.Medios,
            icon: NewspaperIcon
          }
        ]
      }
    ]
  }
]

export const MOBILE_MENU_SECTIONS = [
  {
    id: 'storage-types',
    translationKey: 'MENU.STORAGE.SECTIONS.STORAGE_TYPES',
    items: [
      {
        translationKey: 'MINI_STORAGE',
        href: `/${PillarPage.SelfStorage}`
      },
      {
        translationKey: 'COMMERCIAL_WAREHOUSE',
        href: `/${PillarPage.Warehouse}`
      },
      {
        translationKey: 'PARKING',
        href: `/${PillarPage.Parking}`
      },
      {
        translationKey: 'INDUSTRIAL',
        href: `/${PillarPage.Industrial}`
      }
    ]
  }
]

// --- Drawer navigation constants ---

export type DrawerLink = {
  translationKey: string
  url: string
  icon: string // si- class name
  badge?: (counts: INotificationCounts) => number
  /** Visibility gate; resolved at render time by the consumer. */
  gateBy?: MenuGate
}

/** What a private renter comes for. */
export const DRAWER_PERSONAL_LINKS: DrawerLink[] = [
  {
    translationKey: 'NAV_DRAWER.LINK_MINI_STORAGE',
    url: `/${PillarPage.SelfStorage}`,
    icon: 'si-box'
  },
  {
    translationKey: 'NAV_DRAWER.LINK_ON_DEMAND',
    url: ON_DEMAND_PATH,
    icon: 'si-delivery-truck',
    gateBy: 'on-demand-coverage'
  },
  {
    translationKey: 'NAV_DRAWER.LINK_PARKING',
    url: `/${PillarPage.Parking}`,
    icon: 'si-vehicle-entrance'
  }
]

/** B2B space — fleets, commercial, 3PL, industrial. */
export const DRAWER_BUSINESS_LINKS: DrawerLink[] = [
  {
    translationKey: 'NAV_DRAWER.LINK_FLEET',
    url: FLEET_PATH,
    icon: 'si-garage'
  },
  {
    translationKey: 'NAV_DRAWER.LINK_WAREHOUSE',
    url: `/${PillarPage.Warehouse}`,
    icon: 'si-warehouse'
  },
  {
    translationKey: 'NAV_DRAWER.LINK_LOGISTICS',
    url: RedirectUrls.SolucionesLogisticas,
    icon: 'si-container'
  },
  {
    translationKey: 'NAV_DRAWER.LINK_INDUSTRIAL',
    url: `/${PillarPage.Industrial}`,
    icon: 'si-industrial-ship'
  }
]

/** Storage city pages — same four the desktop mega menu features. Labels
 *  reuse the mega-menu keys so the two maps cannot drift. */
export const DRAWER_CITY_LINKS: DrawerLink[] = [
  {
    translationKey: 'MENU.STORAGE.SECTIONS.LOCATIONS.ITEMS.MONTERREY.LABEL',
    url: `/${PillarPage.SelfStorage}/${SearchPageCity.Monterrey}`,
    icon: 'si-location-dot'
  },
  {
    translationKey: 'MENU.STORAGE.SECTIONS.LOCATIONS.ITEMS.CDMX.LABEL',
    url: `/${PillarPage.SelfStorage}/${SearchPageCity.CiudadDeMexico}`,
    icon: 'si-location-dot'
  },
  {
    translationKey: 'MENU.STORAGE.SECTIONS.LOCATIONS.ITEMS.GUADALAJARA.LABEL',
    url: `/${PillarPage.SelfStorage}/${SearchPageCity.Guadalajara}`,
    icon: 'si-location-dot'
  },
  {
    translationKey: 'MENU.STORAGE.SECTIONS.LOCATIONS.ITEMS.QUERETARO.LABEL',
    url: `/${PillarPage.SelfStorage}/${SearchPageCity.Queretaro}`,
    icon: 'si-location-dot'
  }
]
